import React, { useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteResource,
  getAllResources,
  addResource,
} from "../../../../store/api/resources";
import { allowedTypes, isValidFileType } from "../../../../constants/types";
import FileViewerModal from "./ViewFile";
import CommonSearch from "../../../common/Search";
import Button from "../../../common/Button";

const ResourceList = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success, error, warning, info
  });

  const { data: resources, isLoading } = useQuery({
    queryKey: ["resourcesList"],
    queryFn: () => getAllResources({ IsOnlyImage: false }),
  });

  const { mutate: deleteResourceMutate } = useMutation({
    mutationFn: deleteResource,
    onSuccess: (data) => {
      queryClient.refetchQueries("resourcesList");
      setSnackbar({
        open: true,
        message: "Resource deleted successfully!",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Failed to delete content",
        severity: "error",
      });
    },
  });

  const { mutate: addResourceMutate } = useMutation({
    mutationFn: addResource,
    onSuccess: (data) => {
      queryClient.refetchQueries("resourcesList");
      setIsUploading(false);
      setSnackbar({
        open: true,
        message: "File uploaded successfully!",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Failed to add content",
        severity: "error",
      });
      setIsUploading(false);
    },
  });

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && isValidFileType(file)) {
      setIsUploading(true);

      try {
        const base64Data = await convertFileToBase64(file);

        const fileData = {
          fileName: file.name,
          contentType: file.type,
          fileData: base64Data,
        };
        addResourceMutate(fileData);
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Upload failed. Please try again.",
          severity: "error",
        });
        setIsUploading(false);
      }
    } else {
      setSnackbar({
        open: true,
        message: "Please select a valid file type",
        severity: "warning",
      });
    }

    // Reset the file input value so the same file can be selected again
    event.target.value = "";
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddFilesClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleView = (resource) => {
    setSelectedResource(resource);
    setModalOpen(true);
  };

  const handleDelete = (resourceId) => {
    deleteResourceMutate(resourceId);
  };

  const formatFileSize = (size) => {
    return size.toFixed(3);
  };

  return (
    <Box sx={{ p: 3 }}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
        accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.png,.zip,.ppt,.pptx,.mp4,.mov"
        disabled={isUploading}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          Resources
        </Typography>

        <Button
          onClick={handleAddFilesClick}
          text={isUploading ? "UPLOADING..." : "ADD FILES"}
          endIcon={<AddIcon />}
          disabled={isUploading}
          loading={isUploading}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Allowed file types: pdf, docx, doc, xlsx, xls, jpg, png, zip, ppt, pptx,
        mp4, mov
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You are currently using:{" "}
        {/* {resources?.reduce((acc, curr) => acc + curr.size, 0).toFixed(2)} mb of
        disk */}
      </Typography>

      <CommonSearch
        placeholder="Search resources"
        onSearch={setSearchQuery}
        initialValue={searchQuery}
        sx={{ mb: 3 }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell align="right">File Size (MB)</TableCell>
              <TableCell align="right">Date Added</TableCell>
              <TableCell align="right">View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resources?.paginatedContent.map((resource) => (
              <TableRow key={resource.id}>
                <TableCell>{resource.title}</TableCell>
                <TableCell align="right">
                  {formatFileSize(resource.fileSize)}
                </TableCell>
                <TableCell align="right">{resource.dateAdded}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleView(resource)}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(resource.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <FileViewerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fileData={selectedResource}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResourceList;
